import type { BookingSettings } from '@/types'

/**
 * EP-136 (T136-026) — client-side mirror of the resolution rules in
 * `Property#resolved_booking_settings` (api/app/models/property.rb).
 *
 * The server does a PER-KEY merge of the property's `booking_settings` over
 * the account's `settings.booking_settings` — never a whole-hash shadow
 * (that pattern is a documented production bug for `payment_methods`: a
 * property that only sets one payment method used to silently lose every
 * account-level payment method). This file exists so the property-form
 * "inherited vs overridden" UI and its submit payload can't drift from that
 * same per-key rule.
 */

// Mirrors Property::DEFAULT_BOOKING_FEE_MODE / DEFAULT_BOOKING_FEE_FORFEIT_ON_CANCEL.
// `booking_fee_default` has NO built-in default on either side of the stack —
// an unset default just means the operator types the amount at booking time.
export const DEFAULT_BOOKING_FEE_MODE = 'deposit' as const
export const DEFAULT_BOOKING_FEE_FORFEIT_ON_CANCEL = true

export interface ResolvedBookingSettings {
  mode: 'deposit' | 'revenue'
  default: number | undefined
  forfeitOnCancel: boolean
}

/** The account-level effective values, hardcoded defaults applied. */
export function resolveAccountBookingSettings(
  accountBookingSettings: BookingSettings | null | undefined,
): ResolvedBookingSettings {
  const acct = accountBookingSettings ?? {}
  return {
    mode: acct.booking_fee_mode ?? DEFAULT_BOOKING_FEE_MODE,
    default: acct.booking_fee_default,
    forfeitOnCancel: acct.booking_fee_forfeit_on_cancel ?? DEFAULT_BOOKING_FEE_FORFEIT_ON_CANCEL,
  }
}

export interface BookingSettingsFormDefaults {
  booking_fee_mode_override: boolean
  booking_fee_mode: 'deposit' | 'revenue'
  booking_fee_default_override: boolean
  booking_fee_default: number | undefined
  booking_fee_forfeit_on_cancel_override: boolean
  booking_fee_forfeit_on_cancel: boolean
}

/**
 * Per-key form defaults for the property-level Booking block.
 *
 * `*_override` is true only when THIS property's `booking_settings` hash
 * actually carries that key — the only thing the per-key merge cares about.
 * The paired value field always resolves to a concrete starting value (the
 * property's own override if present, else the resolved account default) so
 * flipping "override" on always reveals something sensible with no
 * seed-on-toggle handler — contrast the `payment_methods` override
 * precedent, whose state starts blank and must be seeded manually onChange.
 */
export function resolveBookingSettingsFormDefaults(
  propertyBookingSettings: BookingSettings | null | undefined,
  accountBookingSettings: BookingSettings | null | undefined,
): BookingSettingsFormDefaults {
  const prop = propertyBookingSettings ?? {}
  const resolved = resolveAccountBookingSettings(accountBookingSettings)

  return {
    booking_fee_mode_override: prop.booking_fee_mode != null,
    booking_fee_mode: prop.booking_fee_mode ?? resolved.mode,
    booking_fee_default_override: prop.booking_fee_default != null,
    booking_fee_default: prop.booking_fee_default ?? resolved.default,
    booking_fee_forfeit_on_cancel_override: prop.booking_fee_forfeit_on_cancel != null,
    booking_fee_forfeit_on_cancel: prop.booking_fee_forfeit_on_cancel ?? resolved.forfeitOnCancel,
  }
}

export interface BookingSettingsFormValues {
  booking_fee_mode_override?: boolean
  booking_fee_mode?: 'deposit' | 'revenue'
  booking_fee_default_override?: boolean
  booking_fee_default?: number
  booking_fee_forfeit_on_cancel_override?: boolean
  booking_fee_forfeit_on_cancel?: boolean
}

/**
 * Builds the `properties.booking_settings` PATCH payload from the form's
 * flat override + value fields: a key is present ONLY when its override flag
 * is on. `booking_settings` is a plain jsonb column with full-replace
 * semantics on save (no server-side merge for the property's own row), so
 * this must emit the COMPLETE set of active overrides every save — omitting
 * a previously-overridden key here is how the user reverts that key back to
 * inheriting the account default.
 */
export function buildBookingSettingsPayload(form: BookingSettingsFormValues): BookingSettings {
  const payload: BookingSettings = {}

  if (form.booking_fee_mode_override && form.booking_fee_mode) {
    payload.booking_fee_mode = form.booking_fee_mode
  }
  if (
    form.booking_fee_default_override &&
    form.booking_fee_default !== undefined &&
    !Number.isNaN(form.booking_fee_default)
  ) {
    payload.booking_fee_default = form.booking_fee_default
  }
  if (form.booking_fee_forfeit_on_cancel_override && form.booking_fee_forfeit_on_cancel !== undefined) {
    payload.booking_fee_forfeit_on_cancel = form.booking_fee_forfeit_on_cancel
  }

  return payload
}
